import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createAssessment, resumeAssessment, getDepthOptions, getCurrentQuestion, getAssessment, selectManagerLevel, abandonAssessment } from '../api/assessment';
import { CenteredLayout } from '../components/PageLayout';
import Card from '../components/Card';
import Button from '../components/Button';
import Alert from '../components/Alert';
import useProfileGuard from '../hooks/useProfileGuard';
import ProfileNudgeModal from '../components/ProfileNudgeModal';
import AssessmentPreparingScreen from '../components/AssessmentPreparingScreen';
import { MANAGER_LEVELS } from '../components/managerLevels';
import Modal from '../components/Modal';

// 2026-05-09：题量 / 轮数从后端策略读取，不再前端硬编码
const DEPTH_META = [
  { value:'quick',    label:'快速版', desc:'快速了解 AI 能力概况' },
  { value:'standard', label:'标准版', desc:'全面评估，推荐首次测评', recommended:true },
  { value:'deep',     label:'深度版', desc:'深度分析，细化薄弱项' },
];

// V5（2026-05-19）：题量 30/60/90，对应 quick/standard/deep —— 见 docs/V5-升级规划.md §1.4。
// rounds 字段对齐后端 round_plan.round_count() —— 每能力轮 8 题，3/6/9 轮。
// 后端 getDepthOptions() 会覆盖；这里是后端不可达时的兜底默认。
const DEFAULT_DEPTH_OPTIONS = {
  quick:    { total_questions: 30, rounds: 3 },
  standard: { total_questions: 60, rounds: 6 },
  deep:     { total_questions: 90, rounds: 9 },
};

// S 层级（管理层级，被测者自选；值=枚举码，与后端 Assessment.ManagerLevel 一致）。
// 「重点考察」为定性展示，不标阶位——真源主战层期望在 backend defaults.py:116。

export default function AssessmentRulesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const taskId = new URLSearchParams(location.search).get('assessment_id');
  const [entryTask, setEntryTask] = useState(null);
  const [entryLoading, setEntryLoading] = useState(!!taskId);
  const [conflictId, setConflictId] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState('');
  const [selected, setSelected] = useState('standard');
  const [managerLevel, setManagerLevel] = useState('');   // 空=未选；必选
  const mlRefs = useRef([]);                                // S 卡 DOM，用于方向键移焦
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [preparingId, setPreparingId] = useState(null);
  const startPendingRef = useRef(false);
  const [error, setError] = useState('');
  const [resumeData, setResumeData] = useState(null);
  const [depthOptions, setDepthOptions] = useState(DEFAULT_DEPTH_OPTIONS);

  const { guardOpen, checkAndGuard, closeGuard } = useProfileGuard();

  const isEnterprise = entryTask ? entryTask.mode === 'enterprise' : !!user?.enterprise_name;
  const enterpriseName = entryTask?.enterprise_name || user?.enterprise_name;

  useEffect(() => {
    let stopped = false;
    setEntryTask(null);
    setEntryLoading(!!taskId);
    setManagerLevel('');
    if (taskId) {
      getAssessment(taskId).then(({ data }) => {
        if (!stopped) {
          setEntryTask(data);
          setManagerLevel(data.manager_level || '');
        }
      }).catch(err => {
        if (!stopped) setError(err.response?.data?.detail || '暂时无法读取测评，请刷新重试');
      }).finally(() => { if (!stopped) setEntryLoading(false); });
    }
    return () => { stopped = true; };
  }, [taskId]);

  // 把 DEPTH_META 与后端返回的题量配置合并为渲染列表
  const DEPTHS = DEPTH_META.map(d => {
    const opt = depthOptions[d.value] ?? DEFAULT_DEPTH_OPTIONS[d.value];
    return {
      ...d,
      count: `${opt.total_questions}题`,
      rounds: `${opt.rounds}轮`,
      time: `${Math.round(opt.total_questions / 2)}min`,
    };
  });

  useEffect(() => {
    // V5/M6b：极简画像不再 gate 测评。历史 Phase G 的 job_category_code 校验已随
    // 后端 migration 0013 删除字段一并下线。

    resumeAssessment().then(({ data }) => {
      if (data.has_unfinished) setResumeData(data.assessment);
    }).catch(() => {});

    getDepthOptions()
      .then(({ data }) => { if (data?.options) setDepthOptions(data.options); })
      .catch(() => { /* 静默失败保留默认 */ });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!preparingId) return undefined;
    let stopped = false;
    let timer;
    const controller = new AbortController();
    // 记录已创建的测评，断网后重试只继续等待，不能再次创建。
    setResumeData({ id: preparingId });
    const pollFirstQuestion = async () => {
      try {
        const { data } = await getCurrentQuestion(preparingId, { signal: controller.signal });
        if (stopped) return;
        if (data.status === 'generating') {
          timer = setTimeout(pollFirstQuestion, 1500);
        } else if ((data.status === 'ok' && data.question?.id) || data.status === 'completed') {
          navigate(`/assessment/${preparingId}`);
          setPreparingId(null);
        } else {
          setError(data.detail || '暂时无法准备题目，请重试');
          setPreparingId(null);
        }
      } catch (err) {
        if (stopped) return;
        if (err.response?.data?.code === 'MANAGER_LEVEL_REQUIRED') {
          navigate('/assessment/rules?' + new URLSearchParams({ assessment_id: preparingId }), { replace: true });
          setPreparingId(null);
          return;
        }
        if (err.response?.data?.code === 'PROFILE_INCOMPLETE') {
          navigate(`/assessment/${preparingId}`);
          setPreparingId(null);
          return;
        }
        setError(err.response?.data?.detail || '暂时无法读取题目准备状态，请重试');
        setPreparingId(null);
      }
    };
    pollFirstQuestion();
    return () => {
      stopped = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [preparingId, navigate]);

  // S radiogroup 键盘：Space/Enter 选当前；方向键移到相邻并选中+移焦
  const handleMlKey = (e, idx) => {
    if (taskId && !entryTask?.needs_manager_level) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      setManagerLevel(MANAGER_LEVELS[idx].value);
      return;
    }
    let next = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (idx + 1) % MANAGER_LEVELS.length;
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (idx - 1 + MANAGER_LEVELS.length) % MANAGER_LEVELS.length;
    if (next !== null) {
      e.preventDefault();
      setManagerLevel(MANAGER_LEVELS[next].value);
      mlRefs.current[next]?.focus();
    }
  };

  const handleStart = async () => {
    if (startPendingRef.current) return;
    // 创建入口遇到未完成测评必须明确提示，由用户决定是否继续。
    if (resumeData && (!taskId || String(resumeData.id) !== taskId)) {
      setConflictId(resumeData.id);
      return;
    }
    if (taskId && !entryTask) return;
    // 先 setLoading(true) 让按钮 disabled,再 guard —— 避免 guard 等待期间用户重复点击
    startPendingRef.current = true;
    setError(''); setLoading(true);
    try {
      if (entryTask && !entryTask.needs_manager_level) {
        setPreparingId(taskId);
        return;
      }
      const status = await checkAndGuard();
      if (status !== 'pass') {
        if (status === 'blocked_error') setError('暂时无法读取画像，请稍后重试');
        // blocked_incomplete: hard modal 已弹,不需要 setError
        // blocked_auth: axios 拦截器接管跳 /login,不显示任何提示
        return;
      }
      setCreating(true);
      if (entryTask) {
        await selectManagerLevel(taskId, managerLevel);
        setPreparingId(taskId);
        return;
      }
      // 企业模式：不传 depth，由后端按 enterprise.rules.depth 决定
      // 个人模式：传员工自选档位
      const payload = {
        ...(isEnterprise ? {} : { depth: selected }),
        manager_level: managerLevel,   // 必选已保证非空
      };
      const { data } = await createAssessment(payload);
      setPreparingId(data.id);
    } catch (err) {
      if (entryTask) {
        if (err.response?.data?.code === 'ASSESSMENT_ENTRY_LOCKED') {
          try {
            const { data } = await getAssessment(taskId);
            setEntryTask(data);
            setManagerLevel(data.manager_level || '');
            setError('该测评状态已更新，请确认后继续。');
          } catch {
            setError('暂时无法读取测评状态，请刷新重试');
          }
        } else {
          setError(err.response?.data?.detail || '保存管理层级失败，请重试');
        }
        return;
      }
      // 创建可能已提交，只是响应超时/丢失；只读核对，不能自动重发 POST。
      if (!err.response) {
        try {
          const { data } = await resumeAssessment();
          if (data.has_unfinished && data.assessment?.id) {
            setPreparingId(data.assessment.id);
            return;
          }
        } catch { /* 保留创建请求的错误，允许用户重试 */ }
        setError(err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT'
          ? '等待测评响应超时，请稍后重试；如已创建，将继续已有测评'
          : '暂时无法连接测评服务，请检查网络后重试');
        return;
      }
      // 竞态兜底：如果 resume 轮询和用户点击之间产生了新 in-progress，仍走跳转分支
      if (err.response?.status === 409) {
        if (err.response.data?.assessment_id) {
          setResumeData({ id: err.response.data.assessment_id });
          setConflictId(err.response.data.assessment_id);
          return;
        }
        setError(err.response?.data?.detail || '创建测评失败，请重试');
      } else {
        setError(err.response?.data?.detail || '创建测评失败，请重试');
      }
    } finally {
      startPendingRef.current = false;
      setLoading(false);
      setCreating(false);
    }
  };

  const discardAndStartNew = async () => {
    if (discarding || !conflictId) return;
    setDiscarding(true);
    setDiscardError('');
    try {
      const status = await checkAndGuard();
      if (status !== 'pass') {
        if (status === 'blocked_incomplete') {
          setConfirmDiscard(false);
          setConflictId(null);
        } else if (status === 'blocked_error') setDiscardError('暂时无法读取画像，请稍后重试');
        return;
      }
      await abandonAssessment(conflictId);
      setResumeData(null);
      setConflictId(null);
      setConfirmDiscard(false);
      // 与首页一致，确认丢弃后回到普通规则页，等待用户选择并点击开始。
      if (taskId) navigate('/assessment/rules');
    } catch (err) {
      setDiscardError(err.response?.data?.detail || '操作失败，请稍后再试');
    } finally {
      setDiscarding(false);
    }
  };

  if (loading || preparingId || entryLoading) {
    return <CenteredLayout maxWidth={620}><AssessmentPreparingScreen checking={!creating && !preparingId} /></CenteredLayout>;
  }

  return (
    <CenteredLayout maxWidth={620}>
      <div style={{textAlign:'center',marginBottom:32}}>
        <h1 style={{fontSize:26,fontWeight:800,marginBottom:8,color:'#0e1730',letterSpacing:'0.01em'}}>开始测评</h1>
        <p style={{color:'#6a7596',fontSize:13.5,margin:0}}>
          {isEnterprise ? `企业模式 · ${enterpriseName}` : '个人模式 · 评估你在 5 项 AI 领导力上的成熟度'}
        </p>
      </div>

      {resumeData && (!taskId || String(resumeData.id) !== taskId) && (
        <Alert type="info">
          你有一次未完成的测评。
          <button onClick={()=>{ setError(''); setPreparingId(resumeData.id); }}
            style={{marginLeft:8,background:'none',border:'none',color:'#4f46e5',cursor:'pointer',fontWeight:600,fontSize:13}}>
            继续上次测评 →
          </button>
        </Alert>
      )}

      {error && <Alert type="error">{error}</Alert>}

      <Card>
        <h3 id="mlevel-label" style={{ fontSize: 15, fontWeight: 800, marginBottom: 6, color: '#0e1730', letterSpacing: '0.01em' }}>你的管理层级</h3>
        <p style={{ fontSize: 12.5, color: '#6a7596', margin: '0 0 14px' }}>按你当前的管理范围选择，用于生成岗位匹配度。</p>
        <div role="radiogroup" aria-labelledby="mlevel-label" aria-required="true"
          style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
          {MANAGER_LEVELS.map((m, idx) => {
            const isSelected = managerLevel === m.value;
            const focusable = managerLevel ? isSelected : idx === 0;   // 未选时第一张可 Tab 进入
            return (
              <div
                key={m.value}
                ref={(el) => { mlRefs.current[idx] = el; }}
                role="radio"
                tabIndex={focusable ? 0 : -1}
                aria-checked={isSelected}
                aria-disabled={!!taskId && !entryTask?.needs_manager_level}
                onClick={() => { if (!taskId || entryTask?.needs_manager_level) setManagerLevel(m.value); }}
                onKeyDown={(e) => handleMlKey(e, idx)}
                style={{
                  border: `1.5px solid ${isSelected ? '#4f46e5' : '#d6e0f5'}`,
                  borderRadius: 11, padding: '14px 18px', cursor: 'pointer',
                  background: isSelected ? '#f5f4fb' : '#fff',
                  transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: 14,
                  outline: 'none',
                }}
                onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.18)'; }}
                onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                <span style={{
                  width: 40, height: 40, flexShrink: 0, borderRadius: 10,
                  background: isSelected ? '#4f46e5' : '#eef0fb', color: isSelected ? '#fff' : '#4f46e5',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: 15, fontFamily: 'Consolas, "SF Mono", monospace',
                }}>{m.value}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14.5, color: '#0e1730' }}>{m.role}</div>
                  <div style={{ fontSize: 12.5, color: '#6a7596', marginTop: 2 }}>{m.you}</div>
                  <div style={{ fontSize: 12, color: '#8b86c9', marginTop: 4 }}>重点：{m.focus}</div>
                </div>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${isSelected ? '#4f46e5' : '#c1d0ed'}`,
                  background: isSelected ? '#4f46e5' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isSelected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                </div>
              </div>
            );
          })}
        </div>
        {!isEnterprise && !taskId ? (
          <>
            <h3 id="depth-label" style={{fontSize:15,fontWeight:800,marginBottom:14,color:'#0e1730',letterSpacing:'0.01em'}}>选择测评深度</h3>
            <div role="radiogroup" aria-labelledby="depth-label" style={{display:'flex',flexDirection:'column',gap:10,marginBottom:24}}>
              {DEPTHS.map(d => {
                const isSelected = selected===d.value;
                return (
                <div
                  key={d.value}
                  role="radio"
                  tabIndex={isSelected ? 0 : -1}
                  aria-checked={isSelected}
                  onClick={()=>setSelected(d.value)}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      setSelected(d.value);
                    }
                  }}
                  style={{
                    border:`1.5px solid ${isSelected?'#4f46e5':'#d6e0f5'}`,
                    borderRadius:11, padding:'15px 20px', cursor:'pointer',
                    background:isSelected?'#f3f7ff':'#fff',
                    transition:'all 0.15s ease', display:'flex', alignItems:'center', gap:16,
                    outline:'none',
                  }}
                  onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.18)'; }}
                  onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{
                    width:20,height:20,borderRadius:'50%',border:`2px solid ${isSelected?'#4f46e5':'#c1d0ed'}`,
                    background:isSelected?'#4f46e5':'#fff',flexShrink:0,
                    display:'flex',alignItems:'center',justifyContent:'center',
                  }}>
                    {isSelected && <div style={{width:8,height:8,borderRadius:'50%',background:'#fff'}}/>}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontWeight:700,fontSize:14.5,color:'#0e1730'}}>{d.label}</span>
                      {d.recommended && <span style={{fontSize:10.5,background:'#4f46e5',color:'#fff',padding:'2px 8px',borderRadius:5,fontWeight:600,letterSpacing:'0.04em'}}>推荐</span>}
                    </div>
                    <div style={{fontSize:12.5,color:'#6a7596',marginTop:3}}>{d.desc}</div>
                  </div>
                  <div style={{textAlign:'right',fontSize:12.5,color:'#6a7596'}}>
                    <div style={{fontWeight:700,color:'#0e1730',fontFamily:'Consolas, "SF Mono", monospace'}}>{d.count}</div>
                    <div style={{fontFamily:'Consolas, "SF Mono", monospace'}}>{d.rounds} · {d.time}</div>
                  </div>
                </div>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{marginBottom:24}}>
            <Alert type="info">{taskId ? '测评规则已确定，选择管理层级后继续本次测评。' : '测评规则由企业统一配置，无需单独选择深度。'}</Alert>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {[{label:'模式',val:isEnterprise ? '企业模式' : '个人模式'},{label: isEnterprise ? '企业' : '题量',val:enterpriseName || entryTask?.total_questions}].map(i=>(
                <div key={i.label} style={{background:'var(--color-bg-page)',borderRadius:'var(--radius-sm)',padding:'12px 16px'}}>
                  <div style={{fontSize:12,color:'var(--color-text-muted)',marginBottom:4}}>{i.label}</div>
                  <div style={{fontWeight:600}}>{i.val}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{borderTop:'1px dashed #d6e0f5',paddingTop:18}}>
          <p style={{fontSize:12.5,color:'#6a7596',marginBottom:14,lineHeight:1.6}}>
            测评过程中可以随时退出，下次登录可继续作答；中途跳过的题目不会计入分母。
          </p>
          <Button fullWidth size="lg" onClick={handleStart} loading={loading}
            disabled={taskId ? (!entryTask || (entryTask.needs_manager_level && !managerLevel)) : (!resumeData && !managerLevel)}>开始测评</Button>
        </div>
      </Card>
      <ProfileNudgeModal mode="hard" open={guardOpen} onClose={closeGuard} />
      <Modal open={!!conflictId && !confirmDiscard} onClose={() => setConflictId(null)} title="你有一次未完成的测评"
        footer={<><Button variant="secondary" onClick={() => setConflictId(null)}>取消</Button>
          <Button variant="secondary" onClick={() => { setDiscardError(''); setConfirmDiscard(true); }}>开始新测评</Button>
          <Button onClick={() => { setPreparingId(conflictId); setConflictId(null); }}>继续测评</Button></>}>
        <p>你有一次未完成的测评，可以继续上次测评，也可以确认丢弃后开始新测评。</p>
      </Modal>
      <Modal open={!!conflictId && confirmDiscard} onClose={() => { if (!discarding) setConfirmDiscard(false); }} title="确认开始新测评？"
        footer={<><Button variant="secondary" disabled={discarding} onClick={() => setConfirmDiscard(false)}>取消</Button>
          <Button loading={discarding} onClick={discardAndStartNew}>丢弃并开始新测评</Button></>}>
        <p>开始新测评后，上次未完成测评的进度将被丢弃且无法恢复。确认要继续吗？</p>
        {discardError && <Alert type="error">{discardError}</Alert>}
      </Modal>
    </CenteredLayout>
  );
}
